import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import moment from "moment";
import XMLParser from "react-xml-parser";

import { waitCreditSaleFromRcm } from "../../../Utils/RCMLibrary";
import {
  amountDue,
  insuranceId,
  reservationObject,
  reservedUnitObject,
  contactObject,
  locationCode,
  cardType,
  autopayBit,
  getInsuranceStatus,
  getGateCode,
  addPaymentFailureMessage,
  finishNewTenantNote,
  finalDiscountId,
  get24HourFee,
  locationObject,
} from "../../../Utils/Common";
import {
  countPaymentRetry,
  MAX_RETRY_PAYMENT_COUNT,
  paymentRetryFailureAlert,
  resetNumberOfRetryPayment,
} from "../../../Utils/paygateway";
import { logApiFail, logSSMRequest, logSSMResponse } from "../../../Utils/rollbar";
import { 
  getOrCreateOrderId, 
  createTransactionHistory, 
  updateTransactionStatus, 
  TRANSACTION_STATUS 
} from "../../../Utils/transactionStatus";
import { addGateAccessTimeZone } from "../../../Utils/data";
// import { addTenantNotes } from '../../../Utils/data';
// import { getTenantUnitLeaseDetails, testXLinkEMVResult as _testXLinkEMVResult } from "../../../Utils/data";

class ProcessPaymentEmv extends Component {
  constructor(props) {
    super(props);
    this.state = {
      processingPayment: false,
      paygateway: {},
      nRetryPayment: 0,
      autopayBit: sessionStorage.getItem("autopay_bit") || "0",
      feeId: sessionStorage.getItem("cc_fee_id"),
      feeAmount: sessionStorage.getItem("cc_fee_amount"),
      location: locationObject(),
    };
  }

  async componentDidMount() {
    const nRetryPayment = countPaymentRetry(
      "new_tenant_checkout_process_payment_emv_retry_count",
      this.props.location.search
    );
    this.setState({ nRetryPayment });

    let amount = amountDue();
    const orderId = getOrCreateOrderId('new_tenant_order_id');
    const reservationDetails = reservationObject();

    // Create transaction history record for EMV flow
    await createTransactionHistory({
      order_id: orderId,
      location_code: locationCode(),
      tenant_id: reservationDetails?.tenantId || null,
      amount: parseFloat(amount).toFixed(2),
      payment_method: 'CC_EMV',
      payment_flow: 'new_tenant_emv',
    });

    // Update status to PENDING (waiting for card reader)
    await updateTransactionStatus(orderId, TRANSACTION_STATUS.PENDING, {
      step: 'waiting_for_card_reader',
    });

    waitCreditSaleFromRcm(amount, orderId)
      .then(async (res) => {
        console.log("RCM response =>", res);

        const result = res.XLinkEMVResult;
        if (result && result["RESULT"] === "0") {
          // Update status - card reader succeeded, proceeding to process payment
          await updateTransactionStatus(orderId, TRANSACTION_STATUS.IFRAME_SUBMITTED, {
            step: 'card_reader_success',
            transaction_id: result.TRANSACTIONID,
          });
          this.processPayment(res.XLinkEMVResult);
          this.setState({ processingPayment: true });
        } else {
          const resultMsg = result["RESULTMSG"] || "A server-side error has occurred from RCM.";
          sessionStorage.setItem("payment_failure_message", resultMsg);
          // Update status to FAILED
          await updateTransactionStatus(orderId, TRANSACTION_STATUS.FAILED, {
            step: 'card_reader_failed',
            error: resultMsg,
          });
          // eslint-disable-next-line no-throw-literal
          throw { error: resultMsg };
        }
      })
      .catch(async (err) => {
        logApiFail("https://localsystem.paygateway.com", {amount, orderId}, err);
        // Update status to FAILED
        await updateTransactionStatus(orderId, TRANSACTION_STATUS.FAILED, {
          step: 'card_reader_error',
          error: err.error || err.message || 'Unknown error',
        });
        if (this.state.nRetryPayment >= MAX_RETRY_PAYMENT_COUNT) {
          paymentRetryFailureAlert();
          resetNumberOfRetryPayment("new_tenant_checkout_process_payment_emv_retry_count");
          this.props.history.push("/lease-payment");
          return;
        }
        await addPaymentFailureMessage(reservationObject().tenantId);

        this.props.history.push("/payment-failure", {
          retryPath: "/process-payment-emv?retry=true",
          backPath: "/lease-payment",
        });
      });

    // For testing use this on local environment

    // setTimeout(() => {
    //   this.processPayment(_testXLinkEMVResult);
    //   this.setState({ processingPayment: true });
    // }, 3000);
  }

  async processPayment(cardInfo) {
    const reservationDetails = reservationObject();
    const unitData = reservedUnitObject();
    const contact = contactObject();
    const feeFor24Hours = get24HourFee();

    const requestBody = {
      location_code: locationCode(),
      tenant_id: reservationDetails.tenantId,
      gate_code: getGateCode(),
      unit_id: unitData.uId,
      insurance_id: insuranceId(),
      insurance_status_id: getInsuranceStatus(),
      discount_id: finalDiscountId(),
      start_date: moment().format("L"),
      end_date: moment().add(29, "days").format("L"),
      amount: parseFloat(cardInfo.APPROVEDAMOUNT).toFixed(2),
      payment_method: "CC",
      cc_type: cardType(cardInfo.CARDBRAND, cardInfo.CARDBRANDSHORT),
      cc_no: cardInfo.ACCOUNT, // TODO: address name correctly
      cc_expiration_date: `${cardInfo.EXPMONTH}/${2000 + cardInfo.EXPYEAR * 1}`,
      cc_address: `${contact.address}, ${contact.city}, ${contact.state}`,
      cc_postal_code: contact.zip,
      cc_name: `${contact.first} ${contact.last}`,
      transaction_id: cardInfo.TRANSACTIONID,
      cc_response_code: cardInfo.HOSTRESPONSECODE,
      res_code: reservationDetails.reservationCode,
      dbl_reservation_paid_fee: "0.00",
      autopay_bit: autopayBit(),
      order_id: cardInfo.ORDERID,
      token: cardInfo.ALIAS,
      doc_ref_number: "",
      doc_base64_format: localStorage.getItem("signed_lease_pdf") || "",
    };

    const has24HourFee = feeFor24Hours && feeFor24Hours.id && feeFor24Hours.amount > 0;

    if (has24HourFee) {
      requestBody.fee_id = feeFor24Hours.id
      requestBody.fee_amount = feeFor24Hours.amount
    }

    // Get order ID for status tracking
    const orderId = requestBody.order_id || getOrCreateOrderId('new_tenant_order_id');

    // Update status to CONVERT_RESERVATION_STARTED
    await updateTransactionStatus(orderId, TRANSACTION_STATUS.CONVERT_RESERVATION_STARTED, {
      step: 'convert_reservation_started',
    });

    logSSMRequest("/api/ssm/ConvertReservationtoRentalAndSendLeaseAgreement", requestBody);

    axios
      .post("/api/ssm/ConvertReservationtoRentalAndSendLeaseAgreement", JSON.stringify(requestBody), {
        headers: { "Content-Type": "application/json" },
      })
      .then(async (response) => {
        logSSMResponse("/api/ssm/ConvertReservationtoRentalAndSendLeaseAgreement", response.data, true);
        let data = new XMLParser().parseFromString(response.data);
        console.log("SSM API response => ", data);

        const messageKey = has24HourFee ? "ConvertReservationtoRentalAndSendLeaseAgreement_V2Message" : "ConvertReservationToMoveInMessage"
        const resultMsg = data.getElementsByTagName(messageKey)[0].value;

        if (resultMsg === "Successful") {
          const leaseNumberKey = has24HourFee ? "ConvertReservationtoRentalAndSendLeaseAgreement_V2LeaseNumber" : "ConvertReservationToMoveInLeaseNumber"
          const leaseNumber = data.getElementsByTagName(leaseNumberKey)[0].value;
          sessionStorage.setItem("created_lease_number", leaseNumber);

          // Update status to CONVERT_RESERVATION_FINISHED and then COMPLETED
          await updateTransactionStatus(orderId, TRANSACTION_STATUS.CONVERT_RESERVATION_FINISHED, {
            step: 'convert_reservation_success',
            lease_number: leaseNumber,
          });
          await updateTransactionStatus(orderId, TRANSACTION_STATUS.COMPLETED, {
            step: 'payment_flow_completed',
            lease_number: leaseNumber,
          });

          if (has24HourFee) {
            addGateAccessTimeZone(locationCode(), leaseNumber);
          }
          finishNewTenantNote(reservationObject().tenantId, "RCM Card Reader");
          this.props.history.push("/movein-done");
        } else {
          sessionStorage.setItem("payment_failure_message", resultMsg);
          logSSMResponse("/api/ssm/ConvertReservationtoRentalAndSendLeaseAgreement", response.data, false);
          // Update status to FAILED
          await updateTransactionStatus(orderId, TRANSACTION_STATUS.FAILED, {
            step: 'convert_reservation_failed',
            error: resultMsg,
          });
          throw data;
        }
      })
      .catch(async (e) => {
        logSSMResponse("/api/ssm/ConvertReservationtoRentalAndSendLeaseAgreement", e.response?.data || e.message, false);
        logApiFail("ConvertReservationtoRentalAndSendLeaseAgreement", requestBody, e);
        // Update status to FAILED
        await updateTransactionStatus(orderId, TRANSACTION_STATUS.FAILED, {
          step: 'convert_reservation_error',
          error: e.response?.data?.error || e.message || "Payment processing failed",
        });
        sessionStorage.setItem("payment_failure_message", e.response?.data?.error || e.message || "Payment processing failed");
        this.props.history.push("/payment-failure", {
          retryPath: "/process-payment-emv?retry=true",
          backPath: "/lease-payment",
        });
      });
  }

  render() {
    return (
      <React.Fragment>
        <div className="wrapper wrapper-flex">
          <div className="container">
            <div className="row">
              <div className="col-12 text-center">
                <div className="text-center">
                  {this.state.processingPayment ? (
                    <h1 className="font-weight-bold">Payment Processing</h1>
                  ) : (
                    <h1 className="font-weight-bold">
                      Please tap or insert <br /> your credit card below and <br /> follow the instructions on the
                      screen
                    </h1>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 text-center mt-5">{this.renderCardSVG()}</div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }

  renderCardSVG() {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="280" height="280" viewBox="0 0 560 560">
        <g id="Group_386" data-name="Group 386" transform="translate(-262 -428)">
          <circle
            id="Ellipse_84"
            data-name="Ellipse 84"
            cx="278.5"
            cy="278.5"
            r="278.5"
            transform="translate(262 428)"
            fill="#f0dde0"
          />
          <g
            id="Ellipse_83"
            data-name="Ellipse 83"
            transform="translate(311 477)"
            fill="#fff"
            stroke="#d50037"
            strokeWidth="10"
          >
            <circle cx="229.5" cy="229.5" r="229.5" stroke="none" />
            <circle cx="229.5" cy="229.5" r="224.5" fill="none" />
          </g>
          <g id="pay" transform="translate(430 569.132)">
            <g id="Group_382" data-name="Group 382" transform="translate(141.316 160.115)">
              <g id="Group_381" data-name="Group 381">
                <path
                  id="Path_1213"
                  data-name="Path 1213"
                  d="M323.435,261.393a18.633,18.633,0,0,0-20.934,0,18.422,18.422,0,0,0-10.441-3.2,18.685,18.685,0,0,0,0,37.371,18.423,18.423,0,0,0,10.441-3.2,18.687,18.687,0,1,0,20.934-30.961Zm-4.187,20.882a8.284,8.284,0,0,1-11.682,1.034,7.18,7.18,0,0,1-1.085-1.085,5,5,0,0,0-3.98-1.809,5.13,5.13,0,0,0-3.98,1.861,8.087,8.087,0,0,1-6.409,3,8.361,8.361,0,0,1-8.374-8.322,8.3,8.3,0,0,1,14.731-5.324,5.178,5.178,0,0,0,7.288.775,5.485,5.485,0,0,0,.775-.775,8.292,8.292,0,0,1,12.715,10.648Z"
                  transform="translate(-273.4 -258.175)"
                  fill="#d50037"
                />
              </g>
            </g>
            <g id="Group_384" data-name="Group 384" transform="translate(0 55.2)">
              <g id="Group_383" data-name="Group 383" transform="translate(0)">
                <path
                  id="Path_1214"
                  data-name="Path 1214"
                  d="M220.193,119.5c0-.465.207-.982.207-1.5s-.207-.982-.207-1.447V105.493a21.994,21.994,0,0,0-22.123-21.864h-6.306V77.374A22,22,0,0,0,169.952,55.2H21.916A22.082,22.082,0,0,0,0,77.374v90.869a21.817,21.817,0,0,0,21.761,21.864h6.151v6.254a22.215,22.215,0,0,0,22.071,22.174H198.071a22.293,22.293,0,0,0,22.123-22.174V143.743c0-.465.207-.982.207-1.447s-.207-.982-.207-1.447ZM27.912,105.493V179.77h-6a11.472,11.472,0,0,1-11.578-11.32V77.374A11.748,11.748,0,0,1,21.916,65.538H170a11.661,11.661,0,0,1,11.475,11.837v6.254H49.983A21.95,21.95,0,0,0,27.912,105.493Zm181.944,90.869A11.891,11.891,0,0,1,198.071,208.2H49.983a11.813,11.813,0,0,1-11.733-11.837V147.206H209.856Zm0-59.494H38.249V123.429H209.856Zm.052-23.777H38.249v-7.6A11.615,11.615,0,0,1,49.983,93.966h148.14a11.627,11.627,0,0,1,11.785,11.527v7.6Z"
                  transform="translate(0 -55.2)"
                  fill="#d50037"
                />
              </g>
            </g>
          </g>
        </g>
      </svg>
    );
  }
}

const mapStateToProps = (state) => ({
  account: state.account,
});

export default connect(mapStateToProps)(ProcessPaymentEmv);
